'use client';

import { AIAction, CUSTOM_AI_PLUGIN_KEY } from '@/components/editor/plugins/custom-ai-plugin';
import { useProjectMeta } from '@/hooks/meta/useProjectMeta';
import { useProductMeta } from '@/hooks/meta/useProductMeta';
import { aiWritingService } from '@/services/ai-service';
import { AiPrompt, fetchWritingActions } from '@/services/supabase/ai-prompts';
import { useEditorPlugin, useEditorRef, usePluginOption } from '@udecode/plate/react';
import {
  ArrowDown,
  ArrowUp,
  CheckCircle,
  Copy,
  Edit3,
  FileText,
  GraduationCap,
  GripVertical,
  Lightbulb,
  Loader2,
  Maximize2,
  Minimize2,
  PenTool,
  Replace,
  RotateCcw,
  Sparkles,
  Wand2,
  X
} from 'lucide-react';
import React from 'react';
import { useParams } from 'react-router-dom';
import { Command, CommandGroup, CommandItem, CommandList } from './command';

// AI Action definitions - Updated to match new API specification
const aiActions = [
  {
    action: 'rephrase' as AIAction,
    label: 'Rephrase',
    description: 'Improve clarity and flow',
    icon: <Edit3 className="h-4 w-4" />,
    category: 'enhance'
  },
  {
    action: 'improve' as AIAction,
    label: 'Improve writing',
    description: 'Enhance word choice and structure',
    icon: <Wand2 className="h-4 w-4" />,
    category: 'enhance'
  },
  {
    action: 'grammar_check' as AIAction,
    label: 'Fix grammar',
    description: 'Fix grammar and punctuation',
    icon: <CheckCircle className="h-4 w-4" />,
    category: 'enhance'
  },
  {
    action: 'shorten' as AIAction,
    label: 'Make shorter',
    description: 'Reduce text while preserving meaning',
    icon: <Minimize2 className="h-4 w-4" />,
    category: 'adjust'
  },
  {
    action: 'expand' as AIAction,
    label: 'Expand',
    description: 'Add details and elaboration',
    icon: <Maximize2 className="h-4 w-4" />,
    category: 'adjust'
  },
  {
    action: 'simplify' as AIAction,
    label: 'Simplify',
    description: 'Make more accessible',
    icon: <Lightbulb className="h-4 w-4" />,
    category: 'adjust'
  },
  {
    action: 'formalize' as AIAction,
    label: 'Formalize',
    description: 'Professional tone',
    icon: <GraduationCap className="h-4 w-4" />,
    category: 'adjust'
  },
  {
    action: 'summarize' as AIAction,
    label: 'Summarize',
    description: 'Create concise summary',
    icon: <FileText className="h-4 w-4" />,
    category: 'transform'
  },
  {
    action: 'custom' as AIAction,
    label: 'Custom prompt',
    description: 'Use provided prompt',
    icon: <PenTool className="h-4 w-4" />,
    category: 'generate'
  }
];

// Mapping of actions to instruction prompts for the AI
const ACTION_PROMPTS: Record<AIAction, string> = {
  rephrase: 'Rephrase the following text to improve clarity and flow, while maintaining the original meaning.',
  improve: 'Improve the following text by enhancing word choice, sentence structure, and overall quality.',
  shorten: 'Make the following text more concise and shorter while preserving all key information.',
  expand: 'Expand on the following text by adding more detail, descriptive language, and elaboration.',
  simplify: 'Simplify the following text to make it more accessible and easier to understand.',
  formalize: 'Rewrite the following text in a more formal and professional tone.',
  grammar_check: 'Check and fix any grammatical, spelling, or punctuation errors in the following text.',
  summarize: 'Provide a concise summary of the following text.',
  custom: '', // User will provide custom prompt
};

export function CustomAIMenu() {
  const editor = useEditorRef();
  const { setOptions } = useEditorPlugin({ key: CUSTOM_AI_PLUGIN_KEY });
  const isOpen = usePluginOption({ key: CUSTOM_AI_PLUGIN_KEY }, 'isOpen');
  const anchorElement = usePluginOption({ key: CUSTOM_AI_PLUGIN_KEY }, 'anchorElement');
  const selectedText = usePluginOption({ key: CUSTOM_AI_PLUGIN_KEY }, 'selectedText');
  const isLoading = usePluginOption({ key: CUSTOM_AI_PLUGIN_KEY }, 'isLoading');

  // Get project ID from URL params and fetch project with current story
  const { projectId } = useParams();

  const { currentStoryId } = useProjectMeta(projectId || null);
  const { tenant } = useProductMeta();
  // Reconstruct project object for debug logging later if needed, or remove if unused.
  // The 'project' object was only used for debug logging in this component.
  const project = { current_story_id: currentStoryId };

  // Fetch dynamic actions
  const [dynamicActions, setDynamicActions] = React.useState<AiPrompt[]>([]);
  React.useEffect(() => {
    const loadActions = async () => {
      if (projectId) {
        try {
          // Use currentStoryId if available, otherwise just project scope
          // Note: fetchWritingActions handles scope hierarchy (global -> project -> story -> chapter)
          const actions = await fetchWritingActions(projectId, currentStoryId ? Number(currentStoryId) : undefined);
          setDynamicActions(actions);
        } catch (error) {
          console.error('Failed to load dynamic actions:', error);
        }
      }
    };
    loadActions();
  }, [projectId, currentStoryId]);

  // Local state for streaming and result handling
  const [streamingText, setStreamingText] = React.useState('');
  const [finalResult, setFinalResult] = React.useState('');
  const [currentAction, setCurrentAction] = React.useState<AIAction | null>(null);
  const [showResult, setShowResult] = React.useState(false);
  const [showCustomPrompt, setShowCustomPrompt] = React.useState(false);
  const [customPrompt, setCustomPrompt] = React.useState('');

  // Store the editor selection when custom prompt opens to preserve it
  const [preservedSelection, setPreservedSelection] = React.useState<any>(null);

  // Draggable state
  const [position, setPosition] = React.useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragStart, setDragStart] = React.useState({ x: 0, y: 0 });
  const dialogRef = React.useRef<HTMLDivElement>(null);

  // Define handleClose first to avoid circular dependency
  const handleClose = React.useCallback(() => {
    setStreamingText('');
    setFinalResult('');
    setCurrentAction(null);
    setShowResult(false);
    setOptions({
      isOpen: false,
      anchorElement: null,
      selectedText: '',
      action: null,
      isLoading: false,
    });
  }, [setOptions]);

  // Calculate smart initial position when dialog opens
  React.useEffect(() => {
    if (isOpen && anchorElement) {
      const rect = anchorElement.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Calculate center position relative to selected content
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      // Dialog dimensions (approximate)
      const dialogWidth = 384; // w-96 = 384px
      const dialogHeight = 400; // approximate height

      // Smart positioning: try to center horizontally, position vertically for best visibility
      let x = centerX - dialogWidth / 2;
      let y = centerY - dialogHeight / 2;

      // Keep within viewport bounds with padding
      const padding = 16;
      x = Math.max(padding, Math.min(x, viewportWidth - dialogWidth - padding));
      y = Math.max(padding, Math.min(y, viewportHeight - dialogHeight - padding));

      // If selection is in top half, position below; if bottom half, position above
      if (centerY < viewportHeight / 2) {
        y = rect.bottom + 16; // Position below selection
      } else {
        y = rect.top - dialogHeight - 16; // Position above selection
      }

      // Final bounds check
      y = Math.max(padding, Math.min(y, viewportHeight - dialogHeight - padding));

      setPosition({ x, y });
    }
  }, [isOpen, anchorElement]);

  // Drag handlers
  const handleMouseDown = React.useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget || (e.target as HTMLElement).closest('.drag-handle')) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
      e.preventDefault();
    }
  }, [position]);

  const handleMouseMove = React.useCallback((e: MouseEvent) => {
    if (isDragging) {
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;

      // Keep within viewport bounds
      const padding = 16;
      const maxX = window.innerWidth - 384 - padding; // 384 = dialog width
      const maxY = window.innerHeight - 400 - padding; // 400 = approximate dialog height

      setPosition({
        x: Math.max(padding, Math.min(newX, maxX)),
        y: Math.max(padding, Math.min(newY, maxY)),
      });
    }
  }, [isDragging, dragStart]);

  const handleMouseUp = React.useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add global mouse event listeners for dragging
  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = 'none'; // Prevent text selection while dragging

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.userSelect = '';
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Handle escape key to close dialog
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleClose]);

  const handleActionSelect = React.useCallback(async (action: AIAction, customPromptValue?: string) => {
    // Handle custom action - show prompt input if no prompt provided

    if (action === 'custom' && !customPromptValue) {
      // Preserve the current editor selection before showing custom prompt
      if (editor.selection) {
        setPreservedSelection(editor.selection);
      }
      setCurrentAction(action);
      setShowCustomPrompt(true);
      return;
    }

    if (selectedText || action === 'custom') {
      // Reset states and start processing
      console.log('🔄 Resetting states and starting processing...');
      setStreamingText('');
      setFinalResult('');
      setCurrentAction(action);
      setShowResult(false);
      setShowCustomPrompt(false);
      setOptions({ isLoading: true, action });
      console.log('✅ States reset, isLoading set to true');

      try {
        // Validate required context from metadata store
        if (!projectId) {
          throw new Error('No project context available. Please navigate to a project page.');
        }

        if (!currentStoryId) {
          throw new Error('No story context available. Please select a story.');
        }

        // Debug logging
        console.log('AI Writing Action Context (from useProject):', {
          projectId,
          currentStoryId,
          projectData: project,
          action,
          selectedTextLength: selectedText?.length || 0
        });

        // Get tenant ID
        const tenantId = tenant?.id;

        // Map the action to a prompt or use the custom prompt value
        const actionPrompt = action === 'custom' ? customPromptValue : ACTION_PROMPTS[action];

        // Call the AI writing service streaming API with new format
        const response = await aiWritingService.streamAction(
          action,
          selectedText || '', // Ensure selectedText is at least an empty string
          projectId,
          currentStoryId,
          tenantId || undefined,
          actionPrompt
        );

        // Handle Server-Sent Events streaming response
        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('No response body');
        }

        let result = '';
        const decoder = new TextDecoder();

        console.log('🚀 Starting AI writing action streaming...');

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          result += chunk;

          // Update streaming text in real-time
          setStreamingText(result);
          console.log('🔄 Updated streamingText state with chunk length:', chunk.length);
        }

        console.log('✨ Streaming completed. Final result length:', result.length);

        // Set final result and show options
        setFinalResult(result);
        setShowResult(true);
        setOptions({ isLoading: false });

      } catch (error) {
        console.error('AI action failed:', error);
        setOptions({ isLoading: false });

        // Show error to user
        alert(`AI processing failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  }, [selectedText, setOptions, projectId, currentStoryId]);

  // Handle custom prompt submission
  const handleCustomPromptSubmit = React.useCallback(() => {
    if (customPrompt.trim() && currentAction === 'custom') {
      handleActionSelect('custom', customPrompt.trim());
    }
  }, [customPrompt, currentAction, handleActionSelect]);

  // Handle custom prompt cancel
  const handleCustomPromptCancel = React.useCallback(() => {
    setShowCustomPrompt(false);
    setCustomPrompt('');
    setCurrentAction(null);
    // Restore focus to editor and preserved selection
    setTimeout(() => {
      if (editor) {
        editor.tf.focus();
        // Restore the preserved selection if it exists
        if (preservedSelection) {
          try {
            editor.tf.select(preservedSelection);
          } catch (error) {
            console.warn('Could not restore selection:', error);
          }
        }
      }
      setPreservedSelection(null);
    }, 0);
  }, [editor, preservedSelection]);

  // Action handlers for result options
  const handleReplace = React.useCallback(() => {
    if (finalResult.trim()) {
      if (editor.selection) {
        // Check if there's actually selected text
        if (editor.api.isExpanded()) {
          // Delete the selected text and insert the new content
          editor.tf.delete();
          editor.tf.insertText(finalResult);
        } else {
          // No selection, just insert at cursor
          editor.tf.insertText(finalResult);
        }
      } else {
        // No selection, insert at current position
        editor.tf.insertText(finalResult);
      }
    }
    handleClose();
  }, [finalResult, editor, handleClose]);

  const handleAddBelow = React.useCallback(() => {
    if (finalResult.trim()) {
      try {
        // Get the current block entry
        const blockEntry = editor.api.block({ highest: true });
        if (blockEntry) {
          const [, blockPath] = blockEntry;

          // Create a new paragraph block with the AI result
          const newBlock = {
            type: 'p',
            children: [{ text: finalResult }]
          };

          // Calculate the path for inserting after current block
          const nextPath = [...blockPath.slice(0, -1), blockPath[blockPath.length - 1] + 1];

          // Insert the new block after the current block
          editor.tf.insertNodes(newBlock, {
            at: nextPath,
            select: true
          });
        } else {
          // Fallback: just add at current position
          editor.tf.insertText('\n' + finalResult);
        }
      } catch (error) {
        console.error('Error adding below:', error);
        // Fallback: just add at current position
        editor.tf.insertText('\n' + finalResult);
      }
    }
    handleClose();
  }, [finalResult, editor, handleClose]);

  const handleAddAbove = React.useCallback(() => {
    if (finalResult.trim()) {
      try {
        // Get the current block entry
        const blockEntry = editor.api.block({ highest: true });
        if (blockEntry) {
          const [, blockPath] = blockEntry;

          // Create a new paragraph block with the AI result
          const newBlock = {
            type: 'p',
            children: [{ text: finalResult }]
          };

          // Insert the new block at the current block's position (pushes current block down)
          editor.tf.insertNodes(newBlock, {
            at: blockPath,
            select: true
          });
        } else {
          // Fallback: just add at current position
          editor.tf.insertText(finalResult + '\n');
        }
      } catch (error) {
        console.error('Error adding above:', error);
        // Fallback: just add at current position
        editor.tf.insertText(finalResult + '\n');
      }
    }
    handleClose();
  }, [finalResult, editor, handleClose]);

  const handleCopy = React.useCallback(() => {
    if (finalResult.trim()) {
      navigator.clipboard.writeText(finalResult);
      // Could show a toast here
    }
    handleClose();
  }, [finalResult, handleClose]);

  const handleTryAgain = React.useCallback(() => {
    if (currentAction) {
      handleActionSelect(currentAction);
    }
  }, [currentAction, handleActionSelect]);

  // Filter actions based on whether text is selected
  const availableActions = React.useMemo(() => {
    // Map dynamic actions to menu item format
    const mappedDynamicActions = dynamicActions.map(action => ({
      id: `dynamic-${action.id}`,
      action: 'custom' as AIAction,
      label: action.name,
      description: action.action_label || 'Saved Action',
      icon: <Sparkles className="h-4 w-4 text-purple-500" />,
      category: 'saved',
      prompt: action.content
    }));

    if (!selectedText) {
      // Show custom prompt and saved actions when no text is selected
      const customAction = aiActions.filter(action => action.action === 'custom');
      return [...customAction, ...mappedDynamicActions];
    }
    // Show all actions when text is selected
    return [...aiActions, ...mappedDynamicActions];
  }, [selectedText, dynamicActions]);

  // Group actions by category
  const groupedActions = React.useMemo(() => {
    const groups: Record<string, typeof aiActions> = {};
    availableActions.forEach(action => {
      if (!groups[action.category]) {
        groups[action.category] = [];
      }
      groups[action.category].push(action);
    });
    return groups;
  }, [availableActions]);

  // Debug logging for UI state
  React.useEffect(() => {
    console.log('🎭 UI State:', {
      isOpen,
      isLoading,
      showResult,
      showCustomPrompt,
      streamingTextLength: streamingText?.length || 0,
      finalResultLength: finalResult?.length || 0
    });
  }, [isOpen, isLoading, showResult, showCustomPrompt, streamingText, finalResult]);

  if (!isOpen || !anchorElement) {
    return null;
  }

  return (
    <div
      ref={dialogRef}
      className="fixed z-50 w-96 bg-background border rounded-lg shadow-lg"
      style={{
        left: position.x,
        top: position.y,
        cursor: isDragging ? 'grabbing' : 'default',
      }}
      onMouseDown={handleMouseDown}
      onMouseDownCapture={(e) => {
        // Prevent the dialog from stealing focus when custom prompt is shown
        if (showCustomPrompt && e.target !== e.currentTarget) {
          e.stopPropagation();
        }
      }}
    >
      <Command className="rounded-lg">
        {/* Header with drag handle */}
        <div className="border-b px-3 py-2 drag-handle cursor-grab active:cursor-grabbing">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <GripVertical className="h-4 w-4 text-muted-foreground" />
              <Wand2 className="h-4 w-4 text-primary" />
              <span className="font-medium text-sm">
                {showResult ? `${currentAction} Result` :
                  selectedText ? 'Improve selected text' : 'AI Writing Assistant'}
              </span>
            </div>
            <button
              onClick={handleClose}
              className="h-6 w-6 rounded-sm opacity-70 hover:opacity-100 hover:bg-accent flex items-center justify-center"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
          {selectedText && !showResult && (
            <p className="text-xs text-muted-foreground mt-1 truncate">
              "{selectedText.slice(0, 50)}{selectedText.length > 50 ? '...' : ''}"
            </p>
          )}
        </div>

        {/* Content Area */}
        {showCustomPrompt ? (
          <div className="p-4">
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-foreground">Custom Prompt</label>
                <textarea
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  placeholder="Enter your custom prompt..."
                  className="w-full mt-1 p-2 text-sm border border-border rounded-md resize-none bg-background text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-ring focus:border-transparent"
                  rows={3}
                  autoFocus
                  onMouseDown={(e) => {
                    // Prevent the mousedown from affecting editor selection
                    e.stopPropagation();
                  }}
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleCustomPromptSubmit}
                  disabled={!customPrompt.trim()}
                  className="flex-1 px-3 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Generate
                </button>
                <button
                  onClick={handleCustomPromptCancel}
                  className="px-3 py-2 text-sm rounded-md border border-border hover:bg-accent hover:text-accent-foreground text-foreground"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        ) : isLoading ? (
          <div className="p-4">
            <div className="flex items-center justify-between text-sm text-muted-foreground mb-3">
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                {streamingText ? 'Generating...' : `Processing with ${currentAction}...`}
              </div>
              {streamingText && (
                <div className="text-xs">
                  {streamingText.length} characters
                </div>
              )}
            </div>

            {/* Always show the streaming area, even if empty initially */}
            <div className="bg-muted/50 rounded-md p-3 max-h-60 overflow-y-auto min-h-[100px]">
              {streamingText ? (
                <>
                  <div className="text-sm whitespace-pre-wrap">{streamingText}</div>
                  <div className="inline-block w-2 h-4 bg-primary animate-pulse ml-1"></div>
                </>
              ) : (
                <div className="text-sm text-muted-foreground italic flex items-center justify-center h-full">
                  Waiting for response...
                </div>
              )}
            </div>
          </div>
        ) : showResult ? (
          <div className="p-4">
            {/* Final result preview */}
            <div className="bg-muted/50 rounded-md p-3 max-h-60 overflow-y-auto mb-4">
              <div className="text-sm whitespace-pre-wrap">{finalResult}</div>
            </div>

            {/* Action buttons */}
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={handleReplace}
                  className="flex items-center gap-2 px-3 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  <Replace className="h-4 w-4" />
                  Replace
                </button>
                <button
                  onClick={handleAddBelow}
                  className="flex items-center gap-2 px-3 py-2 text-sm rounded-md border hover:bg-accent"
                >
                  <ArrowDown className="h-4 w-4" />
                  Add below
                </button>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={handleAddAbove}
                  className="flex items-center gap-2 px-3 py-2 text-sm rounded-md border hover:bg-accent"
                >
                  <ArrowUp className="h-4 w-4" />
                  Add above
                </button>
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-2 px-3 py-2 text-sm rounded-md border hover:bg-accent"
                >
                  <Copy className="h-4 w-4" />
                  Copy
                </button>
              </div>

              <button
                onClick={handleTryAgain}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-md border hover:bg-accent"
              >
                <RotateCcw className="h-4 w-4" />
                Try again
              </button>
            </div>
          </div>
        ) : (
          <CommandList className="max-h-80">
            {Object.entries(groupedActions).map(([category, actions]) => (
              <CommandGroup key={category} heading={getCategoryLabel(category)}>
                {actions.map((action) => (
                  <CommandItem
                    key={(action as any).id || action.action} // Use id for dynamic actions, action key for static
                    value={action.label}
                    onSelect={() => handleActionSelect(action.action, (action as any).prompt)}
                    className="flex items-center gap-3 px-3 py-2 cursor-pointer"
                  >
                    {action.icon}
                    <div className="flex-1">
                      <div className="font-medium text-sm">{action.label}</div>
                      <div className="text-xs text-muted-foreground">{action.description}</div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        )}
      </Command>
    </div>
  );
}

function getCategoryLabel(category: string): string {
  const labels = {
    enhance: 'Enhance',
    adjust: 'Adjust',
    transform: 'Transform',
    generate: 'Generate',
    saved: 'Saved Actions'
  };
  return labels[category as keyof typeof labels] || category;
}
